import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import BrandLogo from "../components/BrandLogo";
import { formatApiErrorDetail } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Forgot-password — collects an email, fires off a reset request, then shows
 * a generic confirmation regardless of whether the email matched an account
 * (intentional: prevents enumeration attacks).
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr("");
    try {
      await axios.post(`${API}/auth/forgot-password`,
        { email: email.trim().toLowerCase() },
        { withCredentials: true });
      setSubmitted(true);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <div className="px-6 md:px-12 py-6 border-b border-line">
        <Link to="/" className="flex items-center w-fit">
          <BrandLogo variant="wordmark" size={28} showLink={false} />
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md" data-testid="forgot-password-page">
          <div className="label-eyebrow mb-3">Forgot password</div>
          <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mb-3">
            Reset your access
          </h1>
          {submitted ? (
            <div className="surface p-6 mt-6" data-testid="forgot-password-success">
              <p className="text-white text-sm leading-relaxed">
                If an account with <span className="text-volt">{email}</span> exists, we've just sent a password reset link to that inbox. It's valid for <strong>1 hour</strong>.
              </p>
              <p className="text-ink-muted text-xs mt-4">
                Didn't get it? Check your spam folder, or wait a minute then try again. Make sure you typed the same email you used to sign up.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-2">
                <Link to="/login" className="btn-volt flex-1 text-center" data-testid="forgot-back-to-login">
                  Back to sign in
                </Link>
                <button
                  onClick={() => { setSubmitted(false); setEmail(""); }}
                  className="btn-ghost-line flex-1"
                  data-testid="forgot-try-different-email">
                  Try a different email
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-ink-muted text-sm leading-relaxed mb-8">
                Type the email on your PITCHD account. We'll send a one-time link so you can pick a new password — valid for 1 hour.
              </p>
              <form onSubmit={onSubmit} className="space-y-4">
                <label className="block">
                  <div className="label-eyebrow mb-2">Email</div>
                  <input
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourdomain.com"
                    autoFocus autoComplete="email"
                    className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white"
                    data-testid="forgot-email-input"
                  />
                </label>
                {err && <div className="text-sm text-[#FF3B30]" data-testid="forgot-error">{err}</div>}
                <button type="submit" disabled={submitting || !email}
                  className="w-full btn-volt disabled:opacity-50"
                  data-testid="forgot-submit-btn">
                  {submitting ? "Sending…" : "Send reset link"}
                </button>
              </form>
              <div className="mt-6 text-sm text-ink-muted">
                Remembered it?{" "}
                <Link to="/login" className="text-volt hover:underline" data-testid="forgot-back-login-link">
                  Sign in instead
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
